import React, { useState, useEffect } from 'react';
import { PlusCircle, Trash2 } from 'lucide-react';
import { supabase } from '../../../config';

const IssuanceForm = ({ shareholders, shareClasses, onSubmit, onCancel, addMessage, selectedCompany }) => {
  const [issuances, setIssuances] = useState([
    { 
      roundNumber: '', 
      roundTitle: '', 
      shareholderId: '', 
      shareClassId: '', 
      shares: '', 
      pricePerShare: '', 
      issueDate: new Date().toISOString().split('T')[0], 
      payment_status: 'No' 
    }
  ]);
  const [globalShareTypes, setGlobalShareTypes] = useState([]);

  // Fetch global share type templates (where company_id IS NULL)
  useEffect(() => {
    const fetchGlobalShareTypes = async () => {
      try {
        const { data, error } = await supabase
          .from('equity_share_classes')
          .select('*')
          .is('company_id', null)
          .eq('is_active', true)
          .order('priority', { ascending: true });

        if (error) throw error;
        setGlobalShareTypes(data || []);
      } catch (error) {
        console.error('Error fetching global share types:', error);
        // Fallback if is_active column doesn't exist yet
        try {
          const { data: fallbackData } = await supabase
            .from('equity_share_classes')
            .select('*')
            .is('company_id', null)
            .order('priority', { ascending: true });
          setGlobalShareTypes(fallbackData || []);
        } catch (fallbackError) {
          console.error('Fallback fetch also failed:', fallbackError);
        }
      }
    };

    fetchGlobalShareTypes();
  }, []);

  const addRow = () => {
    setIssuances([...issuances, { 
      roundNumber: '', 
      roundTitle: '', 
      shareholderId: '', 
      shareClassId: '', 
      shares: '', 
      pricePerShare: '', 
      issueDate: new Date().toISOString().split('T')[0], 
      payment_status: 'No' 
    }]);
  };

  const removeRow = (index) => {
    if (issuances.length > 1) {
      const newIssuances = issuances.filter((_, i) => i !== index);
      setIssuances(newIssuances);
    }
  };

  const handleChange = (index, field, value) => {
    const newIssuances = [...issuances];
    newIssuances[index][field] = value;
    setIssuances(newIssuances);
  };

  const handleSubmitAll = async (e) => {
    e.preventDefault();
    let allSuccessful = true;
    
    for (const issuance of issuances) {
      if (!issuance.shareholderId || !issuance.shareClassId || !issuance.shares || !issuance.pricePerShare || !issuance.issueDate || !issuance.roundNumber) {
        addMessage('Please fill all required fields for all issuances.', 'error');
        allSuccessful = false;
        break;
      }
      
      try {
        const issuanceData = {
          company_id: selectedCompany.id,
          shareholder_id: parseInt(issuance.shareholderId),
          share_class_id: parseInt(issuance.shareClassId),
          shares: parseInt(issuance.shares),
          price_per_share: parseFloat(issuance.pricePerShare),
          issue_date: issuance.issueDate,
          round: parseInt(issuance.roundNumber),
          round_description: issuance.roundTitle,
          payment_status: issuance.payment_status
        };
        
        await onSubmit(issuanceData);
      } catch (error) {
        addMessage(`Error adding issuance: ${error.message}`, 'error');
        allSuccessful = false;
        break;
      }
    }
    
    if (allSuccessful) {
      addMessage('All issuances added successfully!', 'success');
      onCancel(); // Close the modal
    }
  };

  // Use global share types if no company-specific classes exist
  const availableShareClasses = (shareClasses && shareClasses.length > 0) 
    ? shareClasses 
    : globalShareTypes;

  // Show message if no share types available at all
  if (!availableShareClasses || availableShareClasses.length === 0) {
    return (
      <div className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">No Share Types Available</h3>
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Share Types Required</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>No share types are configured. Please contact your administrator to set up global share types in AdminHQ.</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmitAll} className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">Add New Share Issuance(s)</h3>
      
      <div className="space-y-4">
        {issuances.map((issuance, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
            {issuances.length > 1 && (
              <button
                type="button"
                onClick={() => removeRow(index)}
                className="absolute top-2 right-2 text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            
            <h4 className="text-md font-medium text-gray-800 mb-3">Issuance {index + 1}</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Round Number *</label>
                <input
                  type="number"
                  value={issuance.roundNumber}
                  onChange={(e) => handleChange(index, 'roundNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  min="1"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Round Title</label>
                <input
                  type="text"
                  value={issuance.roundTitle}
                  onChange={(e) => handleChange(index, 'roundTitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Seed Round, Series A"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shareholder *</label>
                <select
                  value={issuance.shareholderId}
                  onChange={(e) => handleChange(index, 'shareholderId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select Shareholder</option>
                  {shareholders.map(sh => (
                    <option key={sh.id} value={sh.id}>
                      {sh.entity_type === 'Individual' 
                        ? `${sh.first_name || ''} ${sh.last_name || ''}`.trim() || sh.name
                        : sh.name
                      }
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Share Class *
                  {availableShareClasses === globalShareTypes && (
                    <span className="text-xs text-blue-600 ml-1">(Using Global Types)</span>
                  )}
                </label>
                <select
                  value={issuance.shareClassId}
                  onChange={(e) => handleChange(index, 'shareClassId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select Share Class</option>
                  {availableShareClasses.map(sc => (
                    <option key={sc.id} value={sc.id}>{sc.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">No. of Shares *</label>
                <input
                  type="text"
                  value={issuance.shares ? parseInt(issuance.shares).toLocaleString() : ''}
                  onChange={(e) => {
                    // Remove commas and parse as number
                    const numericValue = e.target.value.replace(/,/g, '');
                    if (numericValue === '' || /^\d+$/.test(numericValue)) {
                      handleChange(index, 'shares', numericValue);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g., 10,000"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price per Share ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={issuance.pricePerShare}
                  onChange={(e) => handleChange(index, 'pricePerShare', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  min="0"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date *</label>
                <input
                  type="date"
                  value={issuance.issueDate}
                  onChange={(e) => handleChange(index, 'issueDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                <select
                  value={issuance.payment_status}
                  onChange={(e) => handleChange(index, 'payment_status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="No">Not Paid</option>
                  <option value="Partial">Partially Paid</option>
                  <option value="Yes">Fully Paid</option>
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="flex justify-end space-x-2 mt-6">
        <button
          type="button"
          onClick={addRow}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 flex items-center"
        >
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Row
        </button>
        <button 
          type="button" 
          onClick={onCancel} 
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          Add Issuance(s)
        </button>
      </div>
    </form>
  );
};

export default IssuanceForm;